// frontend/src/app/components/projects/project-corpus/dialogs/add-papers-dialog.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { BaseModalComponent } from '../../../modals/base-modal/base-modal.component';
import { ImportSourceType } from '../../../../project-corpus/domain/types';
import { BibtexFormComponent } from './bibtex-form.component';
import { GroupCorpusFormComponent } from './group-corpus-form.component';
import { OpenAlexFormComponent } from './openalex-form.component';
import { PdfFormComponent } from './pdf-form.component';
import { SourcePickerComponent } from './source-picker.component';

const STORAGE_KEY = 'project-corpus.lastSource';
const DEFAULT_SOURCE: ImportSourceType = 'OPENALEX';

/**
 * Add-papers wizard dialog. Renders a `SourcePickerComponent` plus one of the
 * four source-specific forms (OpenAlex / BibTeX / PDFs / Group corpus) inside
 * the shared `BaseModalComponent` wrapper.
 *
 * The user's last-picked source is persisted in `sessionStorage`
 * (`project-corpus.lastSource`) so the dialog opens on the same source the
 * next time. Reads/writes are wrapped in try/catch in case storage is
 * unavailable (private windows, certain headless browsers).
 *
 * Closes on:
 *  - ESC key (handled by `BaseModalComponent`).
 *  - Backdrop click (handled by `BaseModalComponent`).
 *  - Each child form emitting `done` (the wizard then closes itself).
 */
@Component({
  selector: 'app-add-papers-dialog',
  standalone: true,
  imports: [
    CommonModule,
    BaseModalComponent,
    SourcePickerComponent,
    OpenAlexFormComponent,
    BibtexFormComponent,
    PdfFormComponent,
    GroupCorpusFormComponent,
  ],
  templateUrl: './add-papers-dialog.component.html',
  styleUrls: ['./add-papers-dialog.component.scss'],
})
export class AddPapersDialogComponent {
  private readonly _open = signal(false);
  readonly selectedSource = signal<ImportSourceType>(this.loadLastSource());

  @Input()
  set open(value: boolean) {
    this._open.set(value);
  }
  get open(): boolean {
    return this._open();
  }

  @Output() openChange = new EventEmitter<boolean>();

  readonly isOpen = this._open.asReadonly();

  close(): void {
    if (!this._open()) return;
    this._open.set(false);
    this.openChange.emit(false);
  }

  onOpenChange(open: boolean): void {
    this._open.set(open);
    this.openChange.emit(open);
  }

  onSourceChange(source: ImportSourceType): void {
    this.selectedSource.set(source);
    this.persistLastSource(source);
  }

  onFormDone(): void {
    this.close();
  }

  private loadLastSource(): ImportSourceType {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (
        stored === 'OPENALEX' ||
        stored === 'BIBTEX' ||
        stored === 'PDF' ||
        stored === 'GROUP_CORPUS'
      ) {
        return stored;
      }
    } catch {
      // sessionStorage unavailable; fall through to default.
    }
    return DEFAULT_SOURCE;
  }

  private persistLastSource(source: ImportSourceType): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, source);
    } catch {
      // sessionStorage unavailable; ignore.
    }
  }
}
